import { Component, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { merge, of, Subscription, BehaviorSubject } from 'rxjs';
import { BreadcrumbService, IBreadcrumb } from '../../../shared/breadcrumb-service/breadcrumb.service';
import { UrlBuilder } from '../../../../core';
import { DateRangeFilterValue, RangeKind } from './../../../../shared/time-frame-filter/timeframe-filter.component';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ReportData, GridFilter, ReportRequestInfo } from './../../interfaces/report-data';
import { GridDetails } from '../../../tracking-overview/interfaces/grid';
import { ReportColumnsService } from './../../services/report-columns-service';
import { ReportsStorageService } from './../../services/reports-storage-service';
import { ActivityLogger } from '../../../../activities/activity-logger';
import { UserActionName } from '../../../../activities/user-action';
import { TrackingGridService } from '../../../../shared/covance-grid/tracking-grid.service';

@Component({
    selector: 'view-scheduled-reports',
    template: require('./view-scheduled-reports.component.html'),
    styles: [require('./view-scheduled-reports.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ViewScheduledReportsComponent implements OnDestroy {

    public data: ReportData[];
    public selectedDateRange: DateRangeFilterValue;
    public timeFrameFiltersubject: BehaviorSubject<DateRangeFilterValue>;
    public searchTerm = new FormControl('');
    public dataLoaded = false;
    public gridDetails: GridDetails;
    public isGeneral = false;
    public isProtocol = false;
    public reportFilter: GridFilter;
    public pagePerItems = [25, 50, 75, 100, 250, 500];
    public timeFrameRanges: RangeKind[] = [
        { key: 'today', value: 'Today', selected: false },
        { key: 'weekly', value: 'Last 7 days', selected: false },
        { key: 'monthly', value: 'Last 30 days', selected: false },
        { key: 'twoMonths', value: 'Last 60 days', selected: false },
        { key: 'quarterly', value: 'Last 90 days', selected: false },
    ];
    private searchSubscription: Subscription;
    private actionName: UserActionName = 'View Scheduled Reports List';

    constructor(
        private breadCrumbService: BreadcrumbService,
        private urlBuilder: UrlBuilder,
        private changeDetectorRef: ChangeDetectorRef,
        private reportsColumnService: ReportColumnsService,
        private reportsStorageService: ReportsStorageService,
        private trackingGridService: TrackingGridService,
        activityLogger: ActivityLogger) {

        this.getActivatedRouteType();
        this.timeFrameFiltersubject = new BehaviorSubject(this.reportsColumnService.getTodayRange());
        const searchTermFlow = merge(
            of(''),
            this.searchTerm.valueChanges
                .pipe(
                    debounceTime(300),
                    distinctUntilChanged()
                )
        );

        this.loadReports(this.selectedDateRange, true);
        this.searchSubscription = searchTermFlow.subscribe((term: string) => {
            this.applySearch(term);
            this.changeDetectorRef.markForCheck();
        });
        activityLogger.logActivity(this.actionName);
    }

    public ngOnDestroy() {
        this.searchSubscription.unsubscribe();
    }

    public timeFrameFilterChange($event: DateRangeFilterValue) {
        if ($event === this.selectedDateRange && $event.rangeType !== 'dateRange') { return; }
        this.dataLoaded = false;
        this.loadReports($event);
        this.selectedDateRange = $event;
    }

    public getHeaderText() {
        return '';
    }

    public reloadScheduledReports($event: boolean) {
        if (!$event) { return; }
        this.gridDetails = null;
        this.dataLoaded = false;
        this.loadReports(this.selectedDateRange);
    }

    private applySearch(term: string) {
        this.gridDetails = null;
        this.dataLoaded = false;
        this.gridDetails = this.trackingGridService.dateFormatter(this.reportsColumnService.applySearchToGrid(term, this.data, this.reportsColumnService.scheduledReportsHeader));
        this.dataLoaded = true;
        this.changeDetectorRef.markForCheck();
    }

    private loadReports(timeFrame: DateRangeFilterValue, loadAllData?: boolean) {
        let requestInfo = this.getReportDataRequestInfo(timeFrame, loadAllData);
        this.reportsStorageService.getScheduledReports(requestInfo).subscribe(gridData => {
            this.gridDetails = this.trackingGridService.dateFormatter({ header: this.reportsColumnService.scheduledReportsHeader, data: gridData });
            this.data = this.gridDetails.data;
            this.changeDetectorRef.markForCheck();
            this.dataLoaded = true;
        });
    }

    private getActivatedRouteType() {
        let routerUrl: IBreadcrumb = {
            label: 'Scheduled Reports',
            url: this.urlBuilder.esa().sampletracking().report().ScheduledReports().url()
        };
        this.breadCrumbService.AddRouterToBreadCrumb(routerUrl);
        if (this.reportsStorageService.gridFilter) {
            this.reportsStorageService.gridFilter.subscribe(filter => this.reportFilter = filter);
        }
    }

    private getReportDataRequestInfo(timeFrame: DateRangeFilterValue, loadAllData?: boolean): ReportRequestInfo {
        if (loadAllData || !timeFrame) {
            return {
                actionName: this.actionName,
                loadAllData: true
            };
        }
        return {
            fromDate: timeFrame.dateBegin,
            toDate: timeFrame.dateEnd,
            actionName: this.actionName
        };
    }
}